import React, { Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const DropdownContainer = props => {
    const {
        details: { name, className, btnClassName, ariaClassName, text, statesHasFeature, symbol, symbolClassName },
        index,
        environment,
        children,
    } = props;

    const listRef = useRef(null);
    const buttonRef = useRef(null);
    const [open, setOpen] = useState(false);

    const handleButtonKeyDown = e => {
        if (e.key === 'Enter') {
            e.preventDefault();

            setOpen(true);
        }
    };

    const onButtonClick = () => {
        setOpen(prev => !prev);
    };

    const closeMenu = () => {
        setOpen(false);

        buttonRef.current?.focus();
    };

    const focusFirstDropdownItem = () => {
        if (listRef.current) {
            const first = listRef.current.firstElementChild;

            first?.querySelector('a')?.focus();
        }
    };

    /**
     *
     * Close the menu if the user clicks outside of it.
     *
     * Mimics native “blur” behavior so the dropdown doesn't stay open.
     *
     */
    useEffect(() => {
        if (open) {
            focusFirstDropdownItem();
        }

        const onClick = e => {
            if (
                open &&
                listRef.current &&
                !listRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', onClick);

        return () => {
            document.removeEventListener('mousedown', onClick);
        };
    }, [open]);

    /**
     *
     * handleListKeyDown
     *
     * Keyboard navigation enhancements:
     * - Escape: closes the menu and returns focus to the button.
     * - Tab / Shift+Tab: moves focus forward/backward through items,
     *   closes the menu when navigating past the first or last item.
     *
     */
    const handleListKeyDown = e => {
        if (e.key === 'Escape') {
            e.preventDefault();

            closeMenu();

            return;
        }

        if (e.key === 'Tab') {
            e.preventDefault();

            const nodes = Array.from(listRef?.current?.children);
            const currentIndex = nodes.indexOf(document.activeElement.closest('li'));

            if ((e.shiftKey && currentIndex <= 0) || (!e.shiftKey && currentIndex === nodes.length - 1)) {
                closeMenu();
            } else {
                const targetNodeIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;

                nodes[targetNodeIndex]?.querySelector('a')?.focus();
            }
        }
    };

    return (
        <Fragment>
            <li className={className}>
                <button
                    ref={buttonRef}
                    onClick={onButtonClick}
                    onKeyDown={handleButtonKeyDown}
                    id={`${name}_${index}`}
                    data-automation-id={name}
                    className={`usa-accordion__button usa-nav__link dropdown__pos ${btnClassName}`}
                    aria-expanded={open}
                    aria-controls={ariaClassName}
                >
                    <span>
                        {symbol && <FontAwesomeIcon key={symbol} className={symbolClassName} icon={symbol} />}
                        <FormattedMessage id={text} />
                    </span>
                    {statesHasFeature && statesHasFeature(environment) && (
                        <FontAwesomeIcon icon="caret-down" data-automation-id={`${name}_${index}_caret`} />
                    )}
                </button>
                {React.createElement(
                    'ul',
                    {
                        className: 'usa-nav__submenu gi-main__nav__submenu',
                        id: ariaClassName,
                        hidden: !open,
                        ref: listRef,
                        onKeyDown: handleListKeyDown,
                    },
                    children,
                )}
            </li>
        </Fragment>
    );
};
